<template>
  <div class="foodleap-facility-finder antialiased">

    <!-- Hero Section -->
    <header class="relative pt-32 pb-20 ff-hero-gradient overflow-hidden">
      <div class="max-w-7xl mx-auto px-8 relative z-10">
        <div class="tw-flex flex-col lg:flex-row items-center gap-16">
          <div class="flex-1 text-center lg:text-left">
            <span
              class="ff-hero-badge tw-inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary-blue/10 text-secondary-blue text-xs font-bold tracking-widest uppercase">
              <Icon name="location-pin" size="12"/>
              AI Powered Facility Finder
            </span>
            <h1 class="ff-hero-title text-5xl md:text-7xl font-bold tracking-tight text-primary-slate mb-8 leading-[1.1] mt-6">
              Find the right facility for your <span class="text-primary-green">food innovation</span>
            </h1>
            <p class="ff-hero-sub text-xl text-primary-slate/70 font-light max-w-xl mb-12 mx-auto lg:mx-0">
              <!--              Your food innovation needs the right infrastructure to scale. Discover and connect with pilot plants, labs, and co-manufacturing facilities matched to your exact process requirements.-->
              Talk to our AI-powered Facility Finder to discover test facilities, labs, and equipment tailored to your specific needs. Scaling from lab to market requires the right
              environment. Find the right infrastructure in seconds, not months.
            </p>
            <div class="ff-hero-btns tw-flex flex-wrap justify-center lg:justify-start gap-4">
              <button
                @click="scrollToFacilityFinder"
                class="fl-btn-launcher tw-flex items-center gap-4 bg-primary-slate text-white pl-6 pr-4 py-4 rounded-full font-bold shadow-xl shadow-primary-slate-20 transition-all group">
                Launch Facility Finder
                <div class="w-10 h-10 bg-primary-green rounded-full tw-flex items-center justify-center text-primary-slate group-hover:scale-110 transition-transform">
                  <Icon name="comments" size="20"/>
                </div>
              </button>
              <button @click="scrollToFacilities"
                      class="ff-btn-secondary bg-brand-light-grey text-primary-slate px-8 py-4 rounded-full font-bold transition-all tw-flex items-center gap-2">
                Browse facilities
                <Icon name="arrow-down"/>
              </button>
            </div>
          </div>
          <!-- Stats -->
          <div class="flex-1 relative w-full max-w-lg lg:max-w-none animate-on-scroll delay-2">
            <div class="grid grid-cols-1 gap-4">
              <div class="ff-stat-card bg-white rounded-lg p-6 shadow-xl border border-brand-light-grey tw-flex items-center gap-4">
                <div class="w-12 h-12 bg-primary-green/10 rounded-xl tw-flex items-center justify-center shrink-0">
                  <Icon name="building" size="24" class="text-primary-green"/>
                </div>
                <div>
                  <div class="text-3xl font-black text-primary-slate">120+</div>
                  <div class="text-sm text-primary-slate/60">verified facilities</div>
                </div>
              </div>
              <div class="ff-stat-card bg-primary-slate rounded-lg p-6 shadow-xl tw-flex items-center gap-4">
                <div class="w-12 h-12 bg-white/10 rounded-xl tw-flex items-center justify-center shrink-0">
                  <Icon name="location-pin" size="24" class="text-white"/>
                </div>
                <div>
                  <div class="text-3xl font-black text-white">14</div>
                  <div class="text-sm text-white/60">countries represented</div>
                </div>
              </div>
              <div class="ff-stat-card bg-secondary-vibrant-green rounded-lg p-6 shadow-xl tw-flex items-center gap-4">
                <div class="w-12 h-12 bg-primary-slate/10 rounded-xl tw-flex items-center justify-center shrink-0">
                  <Icon name="vial" size="24" class="text-primary-slate"/>
                </div>
                <div>
                  <div class="text-3xl font-black text-primary-slate">6</div>
                  <div class="text-sm text-primary-slate/70">facility types</div>
                </div>
              </div>
            </div>
            <div class="ff-blob-1 absolute -top-12 -right-12 w-48 h-48 bg-secondary-blue/10 rounded-full blur-3xl pointer-events-none"></div>
            <div class="ff-blob-2 absolute -bottom-12 -left-12 w-48 h-48 bg-primary-green/10 rounded-full blur-3xl pointer-events-none"></div>
          </div>
        </div>
      </div>
    </header>

    <!-- Facility Type Categories -->
    <section class="py-16 border-b border-brand-light-grey bg-white">
      <div class="max-w-7xl mx-auto px-8">
        <div class="tw-flex flex-col md:flex-row items-center justify-between gap-8 mb-12 animate-on-scroll">
          <h2 class="text-2xl font-bold text-primary-slate">Browse by facility type</h2>
          <button @click="goToRegistration" class="tw-flex items-center gap-2 text-sm font-bold text-primary-slate/50 hover:text-primary-slate transition-colors">
            View all categories
            <Icon name="arrow-right"/>
          </button>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div
            v-for="(type, i) in facilityTypes"
            :key="i"
            @click="goToRegistration"
            class="ff-type-card animate-on-scroll group cursor-pointer bg-brand-light-grey/30 hover:bg-primary-slate rounded-lg p-5 text-center transition-all border border-brand-light-grey hover:border-primary-slate"
            :class="'delay-' + (i % 3 + 1)"
          >
            <div class="w-12 h-12 rounded-xl tw-flex items-center justify-center mx-auto mb-3 transition-all"
                 :class="type.bgClass + ' group-hover:bg-white/10'">
              <Icon :name="type.icon" size="20" :class="type.iconClass + ' group-hover:text-white transition-colors'"/>
            </div>
            <div class="text-sm font-bold text-primary-slate group-hover:text-white transition-colors leading-tight mb-1">{{ type.name }}</div>
            <div class="text-xs text-primary-slate/50 group-hover:text-white/50 transition-colors">{{ type.count }} facilities</div>
          </div>
        </div>
      </div>
    </section>

    <!-- GET STARTED / How it works -->
    <section class="max-w-7xl mx-auto px-8 py-24 border-brand-light-grey">
      <span class="tw-inline-flex items-center px-4 py-1.5 rounded-full bg-secondary-blue/10 text-secondary-blue text-xs font-bold tracking-widest uppercase"
            ref="facilityFinder">
            Get Started
          </span>
      <h2 class="text-4xl md:text-5xl font-bold tracking-tight text-primary-slate leading-[1.2] mt-2">
        Describe what you need. We'll find it.
      </h2>
      <p class="text-lg text-primary-slate/70 font-light leading-relaxed">
        Tell our Facility Finder exactly what you're looking for, type of food innovation, envisaged process, challenges, location, certifications and get matched with the
        right facilities in seconds.
      </p>
      <div class="tw-flex lg:flex-row gap-16 mt-16">
        <div class="space-y-6 w-1/4">
          <div v-for="(step, i) in steps" :key="i" class="tw-flex items-start gap-4">
            <div class="w-8 h-8 rounded-full bg-primary-slate text-white text-sm font-black tw-flex items-center justify-center shrink-0">{{ i + 1 }}</div>
            <div>
              <h4 class="font-bold text-primary-slate mb-1">{{ step.title }}</h4>
              <p class="text-sm text-primary-slate/60">{{ step.description }}</p>
            </div>
          </div>
        </div>
        <div class="items-start relative delay-2 w-3/4">
          <iframe class="border rounded-xl" src="https://platform.foodleap.com/chatbot/2/embed?apiKey=ds_chat_vGolUvVsPfSh2CjDSCTg" width="100%" height="700" frameborder="0"
                  allow="clipboard-write"></iframe>
          <div class="absolute -top-6 -right-6 w-24 h-24 bg-secondary-blue/10 rounded-full blur-2xl pointer-events-none"></div>
          <div class="absolute -bottom-6 -left-6 w-24 h-24 bg-primary-green/10 rounded-full blur-2xl pointer-events-none"></div>
        </div>
      </div>
    </section>

    <!-- Featured Facilities -->
    <section ref="facilitiesSection" class="py-24 bg-brand-light-grey/20">
      <div class="max-w-7xl mx-auto px-8">
        <div class="tw-flex flex-col md:flex-row justify-between items-end mb-16 gap-6 animate-on-scroll">
          <div>
            <span class="text-primary-slate/40 font-black tracking-widest text-xs uppercase mb-3 block">On the platform</span>
            <h2 class="text-4xl md:text-5xl font-bold tracking-tight text-primary-slate">Featured facilities</h2>
            <p class="text-primary-slate/60 mt-4 max-w-md">Leading organisations in the food system transition that offer infrastructure access to innovators.</p>
          </div>
          <button @click="goToRegistration"
                  class="ff-btn-secondary shrink-0 bg-white border border-brand-light-grey text-primary-slate px-6 py-3 rounded-full font-bold transition-all tw-flex items-center gap-2">
            Unlock all 120+
            <Icon name="padlock-unlocked"/>
          </button>
        </div>
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="(facility, i) in facilities"
            :key="i"
            class="ff-facility-card animate-on-scroll bg-white rounded-lg border border-brand-light-grey p-6 shadow-sm cursor-pointer group"
            :class="'delay-' + (i % 3 + 1)"
            @click="goToRegistration"
          >
            <!-- Header -->
            <div class="tw-flex items-start justify-between mb-4">
              <div class="tw-flex items-center gap-3">
                <div class="w-11 h-11 rounded-xl tw-flex items-center justify-center text-white font-bold text-lg shrink-0"
                     :style="{ backgroundColor: facility.color }">
                  {{ facility.name.charAt(0) }}
                </div>
                <div>
                  <div class="font-bold text-primary-slate text-sm leading-tight">{{ facility.name }}</div>
                  <div class="tw-flex items-center gap-1 text-primary-slate/50 text-xs mt-0.5">
                    <Icon name="location-marker"/>
                    {{ facility.location }}
                  </div>
                </div>
              </div>
              <span class="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold"
                    :class="facility.available ? 'bg-primary-green/10 text-primary-green' : 'bg-brand-light-grey text-primary-slate/50'">
                {{ facility.available ? 'Available' : 'On request' }}
              </span>
            </div>
            <!-- Tags -->
            <div class="tw-flex flex-wrap gap-2 mb-4">
              <span
                v-for="tag in facility.tags"
                :key="tag"
                class="px-2.5 py-1 rounded-full bg-brand-light-grey/60 text-primary-slate/70 text-xs font-semibold">
                {{ tag }}
              </span>
            </div>
            <!-- Equipment highlight -->
            <div class="bg-brand-light-grey/30 rounded-md p-3 mb-4">
              <div class="text-[10px] font-black text-primary-slate/40 uppercase tracking-widest mb-2">Key equipment</div>
              <div class="space-y-1">
                <div v-for="eq in facility.equipment" :key="eq" class="tw-flex items-center gap-2 text-xs text-primary-slate/70">
                  <Icon name="check-circle"/>
                  {{ eq }}
                </div>
              </div>
            </div>
            <!-- Footer -->
            <div class="tw-flex items-center justify-between">
              <div class="text-xs text-primary-slate/40 font-semibold">{{ facility.capacity }}</div>
              <button class="tw-flex items-center gap-1.5 text-xs font-bold text-primary-slate/40 group-hover:text-primary-green transition-colors">
                View profile
                <Icon name="arrow-right" class="ff-north-east"/>
              </button>
            </div>
          </div>
          <!-- Locked teaser card -->
          <div
            class="ff-facility-card ff-locked-card animate-on-scroll delay-3 bg-white rounded-lg border border-dashed border-brand-light-grey p-6 shadow-sm cursor-pointer group tw-flex flex-col items-center justify-center text-center gap-4"
            @click="goToRegistration">
            <div class="w-14 h-14 rounded-full bg-brand-light-grey/50 tw-flex items-center justify-center">
              <Icon name="lock" size="24"/>
            </div>
            <div>
              <div class="font-bold text-primary-slate mb-1">117 more facilities</div>
              <div class="text-sm text-primary-slate/50">Sign up to unlock the full directory</div>
            </div>
            <button class="bg-primary-slate text-white px-6 py-2.5 rounded-full text-sm font-bold group-hover:bg-primary-green group-hover:text-primary-slate transition-all">
              Get access
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="py-24 bg-primary-slate relative overflow-hidden">
      <div class="max-w-4xl mx-auto px-8 text-center relative z-10 animate-on-scroll">
        <span class="tw-inline-flex items-center px-4 py-1.5 rounded-full bg-primary-green/10 text-primary-green text-xs font-bold tracking-widest uppercase mb-8">
          Grow the network
        </span>
        <h2 class="text-4xl md:text-5xl font-bold text-white mb-6 leading-[1.15] mt-8">
          Add your facility to the map
        </h2>
        <p class="text-xl text-white/60 mb-12 font-light">
          Get discovered by food innovators looking for your exact capabilities. Join 120+ facilities already on the platform.
        </p>
        <div class="tw-flex flex-wrap justify-center gap-4">
          <button @click="goToRegistration" class="ff-cta-btn bg-primary-green hover:opacity-90 text-primary-slate px-10 py-5 rounded-full font-bold shadow-2xl transition-all">
            Add your facility
          </button>
          <button @click="$router.push({ path: '/about', hash: '#contact' })" class="bg-white/10 hover:bg-white/20 text-white px-10 py-5 rounded-full font-bold transition-colors">
            Talk to us
          </button>
        </div>
      </div>
      <div class="ff-blob-1 absolute top-0 right-0 w-96 h-96 bg-primary-green/5 rounded-full blur-3xl pointer-events-none"></div>
      <div class="ff-blob-2 absolute bottom-0 left-0 w-96 h-96 bg-secondary-blue/5 rounded-full blur-3xl pointer-events-none"></div>
    </section>

    <SimplifiedFooterFoodleapNew/>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import SimplifiedFooterFoodleapNew from '@/components/SimplifiedFooter/SimplifiedFooterFoodleapNew.vue'
  import Icon from '../Icon/Icon.vue'

  export default defineComponent({
    name: 'StaticHomeFoodleapFacilityFinder',
    components: { SimplifiedFooterFoodleapNew, Icon },
    data () {
      return {
        facilityTypes: [
          { name: 'Fermentation', icon: 'vial', count: 34, bgClass: 'bg-primary-green/10', iconClass: 'text-primary-green' },
          { name: 'Extrusion', icon: 'arrow-compress', count: 22, bgClass: 'bg-secondary-blue/10', iconClass: 'text-secondary-blue' },
          { name: 'Cell Cultivation', icon: 'microscope', count: 18, bgClass: 'bg-secondary-vibrant-green/20', iconClass: 'text-primary-slate' },
          { name: 'Food Processing', icon: 'cog', count: 27, bgClass: 'bg-secondary-yellow/10', iconClass: 'text-secondary-yellow' },
          { name: 'Analytical Labs', icon: 'lab', count: 15, bgClass: 'bg-secondary-blue/10', iconClass: 'text-secondary-blue' },
          { name: 'Co-manufacturing', icon: 'wrench', count: 12, bgClass: 'bg-primary-green/10', iconClass: 'text-primary-green' },
        ],
        facilities: [
          {
            name: 'TNO Food & Nutrition',
            location: 'Wageningen, NL',
            color: '#21244A',
            available: true,
            tags: ['Fermentation', 'Pilot scale', 'Protein'],
            equipment: ['500L bioreactors', 'Downstream processing', 'Spray drying'],
            capacity: 'Pilot — 10L to 2,000L',
          },
          {
            name: 'VTT Technical Research',
            location: 'Espoo, FI',
            color: '#5CCC61',
            available: true,
            tags: ['Extrusion', 'Fermentation', 'Cell ag'],
            equipment: ['Twin-screw extruder', 'Cell cultivation lines', 'High-pressure processing'],
            capacity: 'Pilot & Demo scale',
          },
          {
            name: 'NIZO Food Research',
            location: 'Ede, NL',
            color: '#70C1FF',
            available: false,
            tags: ['Dairy', 'Emulsification', 'Sensory'],
            equipment: ['Membrane filtration', 'Emulsion processing', 'Sensory panel lab'],
            capacity: 'Lab to pilot scale',
          },
          {
            name: 'Fraunhofer IVV',
            location: 'Munich, DE',
            color: '#FFC83D',
            available: true,
            tags: ['Packaging', 'Processing', 'Alt-protein'],
            equipment: ['Film extrusion lines', 'Thermoforming', 'Pilot bakery'],
            capacity: 'Pilot to semi-industrial',
          },
          {
            name: 'Roquette Innovation',
            location: 'Lestrem, FR',
            color: '#B5F167',
            available: false,
            tags: ['Wet fractionation', 'Starch', 'Protein'],
            equipment: ['Wet milling', 'Spray dryers', 'Protein isolation'],
            capacity: 'Semi-industrial scale',
          },
          {
            name: 'Wageningen Food Valley',
            location: 'Wageningen, NL',
            color: '#EF374E',
            available: true,
            tags: ['Texturization', 'Fermentation', 'Lab'],
            equipment: ['High-moisture extrusion', 'Fermentation vessels', 'Texture analysis'],
            capacity: 'Lab to pilot scale',
          },
        ],
        aiFeatures: [
          'Specify batch size, technology, and location',
          'Match with certified and verified facilities',
          'Check real-time availability',
          'Connect directly with facility managers',
        ],
        steps: [
          {
            title: 'Describe your needs',
            description: 'Describe your process requirements, scale, and geographic preferences. The more context, the better the match.',
            icon: 'pencil',
            bgClass: 'bg-primary-green/10',
            iconClass: 'text-primary-green',
          },
          {
            title: 'Get matched',
            description: 'Our algorithm scans 120+ facilities and ranks them by fit score, availability, and proximity.',
            icon: 'compass-2',
            bgClass: 'bg-secondary-blue/10',
            iconClass: 'text-secondary-blue',
          },
          {
            title: 'Connect & scale',
            description: 'Reach out directly to facility managers, schedule visits, and start your pilot project.',
            icon: 'hand-shake',
            bgClass: 'bg-secondary-vibrant-green/20',
            iconClass: 'text-primary-slate',
          },
        ],
      }
    },
    methods: {
      goToRegistration () {
        this.$router.push('/register')
      },
      scrollToFacilities () {
        this.$refs.facilitiesSection?.scrollIntoView({ behavior: 'smooth' })
      },
      scrollToFacilityFinder () {
        this.$refs.facilityFinder?.scrollIntoView({ behavior: 'smooth' })
      },
    },
    mounted () {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible')
              this._observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.1 }
      )
      this.$el.querySelectorAll('.animate-on-scroll').forEach(el => {
        this._observer.observe(el)
      })
    },
    beforeUnmount () {
      if (this._observer) this._observer.disconnect()
    },
  })
</script>

<style lang="scss">
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  // ── Keyframes ────────────────────────────────────────────────────────────────

  @keyframes ff-fade-down {
    from {
      opacity: 0;
      transform: translateY(-16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes ff-fade-up {
    from {
      opacity: 0;
      transform: translateY(24px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes ff-float {
    0%, 100% {
      transform: translateY(0px) scale(1);
    }
    50% {
      transform: translateY(-16px) scale(1.04);
    }
  }

  @keyframes ff-float-slow {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  @keyframes ff-shimmer {
    0% {
      background-position: -200% center;
    }
    100% {
      background-position: 200% center;
    }
  }

  // ── Scroll-reveal ─────────────────────────────────────────────────────────────

  .foodleap-facility-finder .animate-on-scroll {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.65s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.65s cubic-bezier(0.22, 1, 0.36, 1);

    &.delay-1 {
      transition-delay: 0.10s;
    }

    &.delay-2 {
      transition-delay: 0.20s;
    }

    &.delay-3 {
      transition-delay: 0.30s;
    }

    &.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
  }

  // ── Component styles ──────────────────────────────────────────────────────────

  .foodleap-facility-finder {
    font-family: 'Inter', sans-serif;
    background-color: #FFFFFF;
    color: #21244A;

    // Brand color variables
    $ps: #21244A; // primary-slate
    $pg: #5CCC61; // primary-green
    $blg: #D9E2DA; // brand-light-grey
    $svg: #B5F167; // secondary-vibrant-green
    $sy: #FFC83D; // secondary-yellow
    $sr: #EF374E; // secondary-red
    $sb: #70C1FF; // secondary-blue

    // ── Color utilities ────────────────────────────────────────────────────────

    .bg-primary-slate {
      background-color: $ps;
    }

    .bg-primary-green {
      background-color: $pg;
    }

    .bg-brand-light-grey {
      background-color: $blg;
    }

    .bg-secondary-vibrant-green {
      background-color: $svg;
    }

    .bg-primary-green\/10 {
      background-color: rgba(92, 204, 97, 0.1);
    }

    .bg-primary-green\/5 {
      background-color: rgba(92, 204, 97, 0.05);
    }

    .bg-secondary-blue\/5 {
      background-color: rgba(112, 193, 255, 0.05);
    }

    .bg-secondary-blue\/10 {
      background-color: rgba(112, 193, 255, 0.1);
    }

    .bg-secondary-vibrant-green\/20 {
      background-color: rgba(181, 241, 103, 0.2);
    }

    .bg-secondary-yellow\/10 {
      background-color: rgba(255, 200, 61, 0.1);
    }

    .bg-primary-slate\/10 {
      background-color: rgba(33, 36, 74, 0.1);
    }

    .bg-brand-light-grey\/20 {
      background-color: rgba(217, 226, 218, 0.2);
    }

    .bg-brand-light-grey\/30 {
      background-color: rgba(217, 226, 218, 0.3);
    }

    .bg-brand-light-grey\/50 {
      background-color: rgba(217, 226, 218, 0.5);
    }

    .bg-brand-light-grey\/60 {
      background-color: rgba(217, 226, 218, 0.6);
    }

    .bg-white\/10 {
      background-color: rgba(255, 255, 255, 0.1);
    }

    .bg-white\/20 {
      background-color: rgba(255, 255, 255, 0.2);
    }

    .text-primary-slate {
      color: $ps;
    }

    .text-primary-green {
      color: $pg;
    }

    .text-secondary-blue {
      color: $sb;
    }

    .text-secondary-yellow {
      color: $sy;
    }

    .text-secondary-vibrant-green {
      color: $svg;
    }

    .text-secondary-red {
      color: $sr;
    }

    .text-primary-slate\/40 {
      color: rgba(33, 36, 74, 0.4);
    }

    .text-primary-slate\/50 {
      color: rgba(33, 36, 74, 0.5);
    }

    .text-primary-slate\/60 {
      color: rgba(33, 36, 74, 0.6);
    }

    .text-primary-slate\/70 {
      color: rgba(33, 36, 74, 0.7);
    }

    .text-primary-slate\/80 {
      color: rgba(33, 36, 74, 0.8);
    }

    .text-white\/50 {
      color: rgba(255, 255, 255, 0.5);
    }

    .text-white\/60 {
      color: rgba(255, 255, 255, 0.6);
    }

    .border-brand-light-grey {
      border-color: $blg;
    }

    .border-brand-light-grey\/50 {
      border-color: rgba(217, 226, 218, 0.5);
    }

    .border-primary-slate {
      border-color: $ps;
    }

    .shadow-primary-slate-20 {
      box-shadow: 0 20px 25px -5px rgba(33, 36, 74, 0.2), 0 8px 10px -6px rgba(33, 36, 74, 0.15);
    }

    .hover\:bg-white\/20:hover {
      background-color: rgba(255, 255, 255, 0.2);
    }

    .hover\:text-primary-slate:hover {
      color: $ps;
    }

    .hover\:text-primary-green:hover {
      color: $pg;
    }

    .hover\:border-primary-slate:hover {
      border-color: $ps;
    }

    // ── Hero ──────────────────────────────────────────────────────────────────

    .ff-hero-gradient {
      background: radial-gradient(circle at top right, $blg 0%, #FFFFFF 60%);
    }

    .ff-hero-badge {
      animation: ff-fade-down 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
    }

    .ff-hero-title {
      animation: ff-fade-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) 0.25s both;
    }

    .ff-hero-sub {
      animation: ff-fade-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) 0.4s both;
    }

    .ff-hero-btns {
      animation: ff-fade-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both;
    }

    .ff-blob-1 {
      animation: ff-float 9s ease-in-out infinite;
    }

    .ff-blob-2 {
      animation: ff-float-slow 11s ease-in-out infinite reverse;
    }

    // ── Stat cards ────────────────────────────────────────────────────────────

    .ff-stat-card {
      transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 0.3s;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 20px 40px rgba(33, 36, 74, 0.1);
      }
    }

    // ── Type cards ────────────────────────────────────────────────────────────

    .ff-type-card {
      transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1),
      background-color 0.2s,
      border-color 0.2s;

      &:hover {
        transform: translateY(-4px);
      }
    }

    // ── Facility cards ────────────────────────────────────────────────────────

    .ff-facility-card {
      transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 0.3s,
      border-color 0.3s;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 16px 40px rgba(33, 36, 74, 0.08);
        border-color: rgba(92, 204, 97, 0.4);
      }
    }

    .ff-locked-card:hover {
      border-color: $blg !important;
      border-style: solid !important;
    }

    .ff-north-east {
      transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .group:hover .ff-north-east {
      transform: translate(3px, -3px);
    }

    // ── Buttons ───────────────────────────────────────────────────────────────

    .fl-btn-launcher {
      transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 20px 40px rgba(33, 36, 74, 0.2);
      }
    }

    .ff-btn-secondary {
      transition: transform 0.2s, background-color 0.2s, box-shadow 0.2s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(33, 36, 74, 0.08);
      }
    }

    .ff-cta-btn {
      position: relative;
      overflow: hidden;
      transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 32px rgba(92, 204, 97, 0.35);
      }
    }

    // ── Chat card ─────────────────────────────────────────────────────────────

    .ff-chat-card {
      animation: ff-float-slow 7s ease-in-out infinite;
    }

    .ff-send-icon {
      transition: transform 0.2s;
      cursor: pointer;

      &:hover {
        transform: translateX(3px) rotate(-15deg);
      }
    }
  }
</style>
